import { useEffect, useState } from "react";
import {
  ArrowUpRight, Brush, ChartLine, Check, ChevronRight, Code, Download, FileText, Gamepad2, Globe, Heart, House, JapaneseYen,
  KeyRound, LayoutGrid, Link2, ListChecks, Mail, Newspaper, NotebookText, Pause, Play, Search, Server, Sparkles, SquarePlay,
  Trash2, Users, Apple, Brain, Cpu, GitBranch, type LucideIcon,
} from "lucide-react";
import {
  cancelMCPOAuth, deleteSkill, installMCP, mcpCatalog, mcpLocalOnly, mcpNeedsAuthorization, mcpOAuthStatus, mcpServers,
  reloadMCP, removeMCP, setSkillEnabled, skillCatalog, skillContent, skillUserAdded, startMCPOAuth, testMCP,
  type MCPCatalogEntry, type MCPServer, type SkillInfo,
} from "@timibot/core";
import { useApp } from "../../context";
import { Dialog, IconBadge, Menu, PillButton, PillSwitcher, PlainRow, SearchField, SectionTitle, Sheet, Spinner, useLongPress, useMenu } from "../../ui/components";
import { SkillNames } from "../chat/names";
import "./skills.css";

/**
 * 侧栏“技能/连接”（弹出页，对应 iOS 的 SkillsTab）：技能（Hermes 会按需自动调用）+ 连接（MCP，让它能用你在其它服务里的账号）。
 */
export function SkillsView({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [page, setPage] = useState<"skills" | "connectors">("skills");
  const [skillQuery, setSkillQuery] = useState("");
  const [connectorQuery, setConnectorQuery] = useState("");
  const [hubSearch, setHubSearch] = useState(0);
  return (
    <Sheet open={open} onClose={onClose} center={<PillSwitcher options={[["skills", "技能"], ["connectors", "连接"]]} value={page} onChange={setPage} />}>
      <div className="skills-search">
        {page === "skills"
          ? <SearchField placeholder="搜索技能" value={skillQuery} onChange={setSkillQuery} onSubmit={() => setHubSearch((n) => n + 1)} />
          : <SearchField placeholder="搜索连接" value={connectorQuery} onChange={setConnectorQuery} />}
      </div>
      {/* 两页都保留着，来回切换不用重新加载 */}
      <div hidden={page !== "skills"}><SkillList query={skillQuery} hubSearch={hubSearch} onClose={onClose} /></div>
      <div hidden={page !== "connectors"}><ConnectorList query={connectorQuery} /></div>
    </Sheet>
  );
}

const categoryIcons: Record<string, LucideIcon> = {
  productivity: ListChecks, creative: Brush, media: SquarePlay, research: Search, "note-taking": NotebookText,
  "social-media": Users, "software-development": Code, devops: Server, web: Globe, email: Mail, apple: Apple,
  "autonomous-ai-agents": Cpu, mlops: Brain, "data-science": ChartLine, github: GitBranch, gaming: Gamepad2,
  "smart-home": House, finance: JapaneseYen, health: Heart,
};

function categoryIcon(raw: string): LucideIcon {
  return categoryIcons[raw.split("/")[0]] ?? LayoutGrid;
}

function skillIcon(x: SkillInfo): LucideIcon {
  const n = x.name.toLowerCase(), c = (x.category ?? "").toLowerCase();
  if (["docx", "pdf"].includes(n) || n.includes("document")) return FileText;
  if (["xlsx", "airtable"].includes(n)) return LayoutGrid;
  if (c.includes("creative") || n.includes("design")) return Brush;
  if (c.includes("media") || n.includes("video")) return SquarePlay;
  if (c.includes("research") || n.includes("feed")) return Newspaper;
  if (c.includes("social")) return Users;
  if (c.includes("email")) return Mail;
  if (c.includes("software") || c.includes("devops") || c.includes("github")) return Code;
  if (c.includes("productivity")) return ListChecks;
  if (c.includes("note")) return NotebookText;
  return Sparkles;
}

function SkillList({ query, hubSearch, onClose }: { query: string; hubSearch: number; onClose: () => void }) {
  const app = useApp();
  const [skills, setSkills] = useState<SkillInfo[]>();
  const [hubResults, setHubResults] = useState<{ name?: string; description?: string; identifier?: string }[]>([]);
  const [searchingHub, setSearchingHub] = useState(false);
  const [searchedHub, setSearchedHub] = useState(false);
  const [installing, setInstalling] = useState<string>();
  const [error, setError] = useState<string>();
  const [detail, setDetail] = useState<SkillInfo>();
  const [pendingDelete, setPendingDelete] = useState<SkillInfo>();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const menu = useMenu();
  const [menuSkill, setMenuSkill] = useState<SkillInfo>();
  const q = query.trim().toLowerCase();

  const load = async () => {
    if (!app.api) return;
    try { setSkills(await skillCatalog(app.api, app.selectedProfile)); setError(undefined); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  useEffect(() => { void load(); }, [app.selectedProfile, app.api]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setHubResults([]); setSearchedHub(false); }, [query]);
  useEffect(() => { if (hubSearch > 0) void searchHub(); }, [hubSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = (skills ?? []).filter((x) => !q || x.name.toLowerCase().includes(q) || (x.description ?? "").toLowerCase().includes(q) || SkillNames.title(x.name).includes(q));
  const mine = filtered.filter(skillUserAdded).sort((a, b) => b.usage - a.usage || a.name.localeCompare(b.name));
  const groups = new Map<string, SkillInfo[]>();
  for (const x of filtered.filter((y) => !skillUserAdded(y))) {
    const k = x.category ?? "other";
    groups.set(k, [...(groups.get(k) ?? []), x]);
  }
  const categories = [...groups.entries()].map(([key, items]) => ({ key, items: items.sort((a, b) => SkillNames.title(a.name).localeCompare(SkillNames.title(b.name), "zh-Hans-CN")) }))
    .sort((a, b) => SkillNames.category(a.key).localeCompare(SkillNames.category(b.key), "zh-Hans-CN"));

  const toggle = async (skill: SkillInfo, enabled: boolean) => {
    if (!app.api) return;
    setSkills((list) => list?.map((x) => x.name === skill.name ? { ...x, enabled } : x));
    try { await setSkillEnabled(app.api, skill.name, enabled, app.selectedProfile); }
    catch (e) {
      setSkills((list) => list?.map((x) => x.name === skill.name ? { ...x, enabled: !enabled } : x));
      app.showToast(e instanceof Error ? e.message : String(e));
    }
  };

  const remove = async (skill: SkillInfo) => {
    if (!app.api) return;
    setDetail(undefined);
    try {
      await deleteSkill(app.api, skill, app.selectedProfile);
      setSkills((list) => list?.filter((x) => x.name !== skill.name));
      app.showToast(`已删除 ${SkillNames.title(skill.name)}`);
      // 技能中心的卸载在服务器后台执行，稍后再刷新一次
      setTimeout(() => void load(), 2000);
    } catch (e) {
      app.showToast(`删除失败：${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const searchHub = async () => {
    if (!app.api || !q || searchingHub) return;
    setSearchingHub(true);
    try { setHubResults(await app.api.searchSkillHub(query.trim(), app.selectedProfile)); }
    catch (e) { app.showToast(e instanceof Error ? e.message : String(e)); }
    finally { setSearchingHub(false); setSearchedHub(true); }
  };

  const install = async (item: { name?: string; identifier?: string }) => {
    const id = item.identifier ?? item.name;
    if (!app.api || !id) return;
    setInstalling(id);
    try {
      const ok = await app.api.installSkill(id, app.selectedProfile);
      app.showToast(ok ? `已安装 ${item.name ?? id}` : "安装未完成");
      await load();
    } catch (e) {
      app.showToast(e instanceof Error ? e.message : String(e));
    } finally {
      setInstalling(undefined);
    }
  };

  const row = (x: SkillInfo, indent = false) => (
    <SkillRow key={x.name} skill={x} indent={indent} onOpen={() => setDetail(x)} onMenu={app.isGuest ? undefined : (pos) => { setMenuSkill(x); menu.show(pos); }} />
  );

  return (
    <div className="skills-list">
      {error && <div className="t-footnote t-danger skills-pad">{error}</div>}
      {!skills && !error && <div className="center-pad"><Spinner /></div>}
      {!q ? (
        <>
          {mine.length > 0 && <><div className="skills-section"><SectionTitle title="我添加的" /></div>{mine.map((x) => row(x))}</>}
          {categories.length > 0 && <div className="skills-section"><SectionTitle title="全部技能" /></div>}
          {categories.map((g) => {
            const isOpen = expanded.has(g.key);
            const Icon = categoryIcon(g.key);
            return (
              <div key={g.key}>
                <PlainRow icon={<Icon size={15} />} title={SkillNames.category(g.key)}
                  onClick={() => setExpanded((s) => { const n = new Set(s); if (n.has(g.key)) n.delete(g.key); else n.add(g.key); return n; })}
                  trailing={<><span className="t-caption t-3 mono">{g.items.length}</span><ChevronRight size={14} className={`t-3 chev${isOpen ? " open" : ""}`} /></>} />
                {isOpen && g.items.map((x) => row(x, true))}
              </div>
            );
          })}
        </>
      ) : (
        <>
          {filtered.map((x) => row(x))}
          {!searchedHub && !searchingHub && <PlainRow icon={<Search size={15} />} title={`在技能中心搜索“${query.trim()}”`} onClick={() => void searchHub()} />}
          {searchingHub && <div className="center-pad"><Spinner /></div>}
          {searchedHub && (
            <>
              <div className="skills-section"><SectionTitle title="技能中心" /></div>
              {!hubResults.length && !searchingHub && <div className="t-footnote t-2 skills-pad">没有找到</div>}
              {hubResults.map((item) => {
                const id = item.identifier ?? item.name ?? "";
                const installed = (skills ?? []).some((x) => x.name === item.name);
                return (
                  <PlainRow key={id} icon={<Download size={15} />} title={item.name ?? id} subtitle={item.description}
                    trailing={installing === id ? <Spinner /> : installed ? <span className="t-caption t-2">已安装</span>
                      : !app.isGuest ? <PillButton title="安装" onClick={() => void install(item)} /> : undefined} />
                );
              })}
            </>
          )}
        </>
      )}
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} align="start" width={170} items={menuSkill ? [
        { label: menuSkill.enabled ? "停用" : "启用", icon: menuSkill.enabled ? <Pause size={16} /> : <Play size={16} />, onSelect: () => void toggle(menuSkill, !menuSkill.enabled) },
        ...(skillUserAdded(menuSkill) ? [{ label: "删除", icon: <Trash2 size={16} />, destructive: true, onSelect: () => setPendingDelete(menuSkill) }] : []),
      ] : []} />
      <SkillDetail skill={detail} onClose={() => setDetail(undefined)} onToggle={(on) => detail && toggle(detail, on)}
        onDelete={() => setPendingDelete(detail)} onUse={() => { if (detail) { app.activeChat.setDraft(`/${detail.name} `); app.setTab("chat"); setDetail(undefined); onClose(); } }} />
      <Dialog open={pendingDelete !== undefined} title={`删除技能“${pendingDelete ? SkillNames.title(pendingDelete.name) : ""}”？`}
        message={pendingDelete?.provenance === "hub" ? "之后可以在技能中心重新安装。" : "会移到服务器的归档里，需要时可以找回。"}
        onClose={() => setPendingDelete(undefined)}
        buttons={[{ title: "取消", role: "cancel" }, { title: "删除", role: "destructive", onClick: () => { if (pendingDelete) void remove(pendingDelete); } }]} />
    </div>
  );
}

function SkillRow({ skill, indent, onOpen, onMenu }: { skill: SkillInfo; indent?: boolean; onOpen: () => void; onMenu?: (pos: { x: number; y: number }) => void }) {
  const press = useLongPress((pos) => onMenu?.(pos));
  const Icon = skillIcon(skill);
  return (
    <div className={indent ? "skill-indent" : ""} {...(onMenu ? press : {})}>
      <PlainRow icon={<Icon size={15} />} title={SkillNames.title(skill.name)} subtitle={skill.enabled ? skill.description : "已停用"} dimmed={!skill.enabled} onClick={onOpen} />
    </div>
  );
}

function SkillDetail({ skill, onClose, onToggle, onDelete, onUse }: {
  skill?: SkillInfo; onClose: () => void; onToggle: (on: boolean) => void; onDelete: () => void; onUse: () => void;
}) {
  const app = useApp();
  const [content, setContent] = useState<string>();
  const [enabled, setEnabled] = useState(true);
  useEffect(() => {
    if (!skill) return;
    setEnabled(skill.enabled);
    setContent(undefined);
    if (app.api) skillContent(app.api, skill.name, app.selectedProfile).then(setContent, () => setContent("（读取失败）"));
  }, [skill?.name]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!skill) return <Sheet open={false} onClose={onClose}>{null}</Sheet>;
  const Icon = skillIcon(skill);
  const provenance = skill.provenance === "bundled" ? "内置" : skill.provenance === "hub" ? "技能中心" : "自己添加";
  return (
    <Sheet open onClose={onClose} size="medium">
      <div className="skill-detail">
        <div className="row" style={{ gap: 14 }}>
          <IconBadge icon={<Icon size={20} />} size={48} />
          <div className="col" style={{ gap: 3 }}>
            <span className="t-title3">{SkillNames.title(skill.name)}</span>
            <span className="t-caption t-3">{skill.usage > 0 ? `${provenance} · 用过 ${skill.usage} 次` : provenance}</span>
          </div>
        </div>
        {skill.description && <p className="t-subheadline t-2" style={{ margin: 0 }}>{skill.description}</p>}
        <div className="form-card">
          {!app.isGuest && (
            <label className="toggle-row">
              <span>启用</span>
              <input type="checkbox" className="switch" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); onToggle(e.target.checked); }} />
            </label>
          )}
          <button className="toggle-row" onClick={onUse}><span>在对话中使用</span><ArrowUpRight size={15} className="t-3" /></button>
          {skillUserAdded(skill) && !app.isGuest && <button className="toggle-row t-danger" onClick={onDelete}><span>删除</span></button>}
        </div>
        <span className="t-headline">说明书</span>
        {content === undefined ? <Spinner /> : <pre className="skill-content mono t-caption t-2">{content}</pre>}
      </div>
    </Sheet>
  );
}

// MARK: 连接（MCP）

export const ConnectorNames = {
  /** 面向普通人的推荐顺序 */
  recommended: ["notion", "canva", "gamma", "todoist", "dropbox", "calendly", "craft", "miro", "figma",
    "fireflies", "wordpress-com", "kiwi", "trivago", "alltrails", "strava", "wolfram", "comfy-cloud", "indeed"],
  names: {
    notion: ["Notion", "读写你的 Notion 页面和数据库"], canva: ["Canva 可画", "创建、搜索和修改 Canva 设计"],
    gamma: ["Gamma", "一句话生成演示文稿、文档和网页"], todoist: ["Todoist", "管理待办事项和项目"],
    dropbox: ["Dropbox", "搜索、读取和整理网盘文件"], calendly: ["Calendly", "预约链接、日程和参会人"],
    craft: ["Craft", "文档、任务和个人知识库"], miro: ["Miro 白板", "读取和编辑白板、流程图"],
    figma: ["Figma", "读取设计稿，生成设计说明"], fireflies: ["Fireflies", "会议录音转写、纪要和待办"],
    "wordpress-com": ["WordPress", "发布和管理博客文章"], kiwi: ["Kiwi 机票", "搜索机票和行程"],
    trivago: ["trivago 酒店", "按城市和日期比价酒店"], alltrails: ["AllTrails", "查找徒步路线和评价"],
    strava: ["Strava", "运动记录和训练趋势"], wolfram: ["Wolfram|Alpha", "数学计算和权威知识查询"],
    "comfy-cloud": ["Comfy Cloud", "在云端生成图片、视频和音频"], indeed: ["Indeed", "搜索招聘职位"],
    amap_travel_weather: ["高德地图", "路线规划、周边搜索、天气"], amap: ["高德地图", "路线规划、周边搜索、天气"],
  } as Record<string, [string, string]>,
  title(name: string): string {
    return this.names[name]?.[0] ?? name.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  },
  subtitle(name: string): string | undefined { return this.names[name]?.[1]; },
  letter(name: string): string { return [...this.title(name)][0]?.toUpperCase() ?? "?"; },
  /** 把常见的技术报错翻译成人话 */
  explain(raw?: string): string {
    if (!raw) return "未知错误";
    const lower = raw.toLowerCase();
    if (lower.includes("no such file or directory")) return "服务器上找不到启动它需要的程序。请更新服务器上的 TimiBot 接入脚本后重试。";
    if (lower.includes("timed out") || lower.includes("timeout")) return "连接超时，稍后再试";
    if (lower.includes("401") || lower.includes("unauthorized") || lower.includes("403")) return "授权失效，请重新授权";
    if (lower.includes("connection refused") || lower.includes("name or service not known")) return "连不上这个服务";
    return raw.length > 120 ? raw.slice(0, 120) + "…" : raw;
  },
};

function ConnectorList({ query }: { query: string }) {
  const app = useApp();
  const [catalog, setCatalog] = useState<MCPCatalogEntry[]>();
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const [showOthers, setShowOthers] = useState(false);
  const [apiKeyFor, setApiKeyFor] = useState<MCPCatalogEntry>();
  const [values, setValues] = useState<Record<string, string>>({});
  const [oauth, setOauth] = useState<{ name: string; flowID: string; url: string }>();
  const menu = useMenu();
  const [menuServer, setMenuServer] = useState<MCPServer>();
  const q = query.trim().toLowerCase();

  const load = async () => {
    if (!app.api) return;
    try {
      const [c, s] = await Promise.all([mcpCatalog(app.api, app.selectedProfile), mcpServers(app.api, app.selectedProfile)]);
      setCatalog(c);
      setServers(s);
      setError(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => { void load(); }, [app.selectedProfile, app.api]); // eslint-disable-line react-hooks/exhaustive-deps

  const matches = (name: string, description = "") => !q || name.includes(q) || description.toLowerCase().includes(q) || ConnectorNames.title(name).toLowerCase().includes(q);
  const connected = servers.filter((x) => matches(x.name));
  const available = (catalog ?? []).filter((e) => !mcpLocalOnly(e) && !servers.some((x) => x.name === e.name) && matches(e.name, e.description));
  const recommended = ConnectorNames.recommended.map((n) => available.find((e) => e.name === n)).filter((e): e is MCPCatalogEntry => !!e);
  const others = available.filter((e) => !ConnectorNames.recommended.includes(e.name));

  const test = async (name: string) => {
    if (!app.api) return;
    setBusy(name);
    try {
      const r = await testMCP(app.api, name, app.selectedProfile);
      app.showToast(r.ok ? `${ConnectorNames.title(name)} 可用，提供 ${r.toolNames.length} 个功能` : r.oauthNeeded ? "需要先授权" : "连接失败：" + ConnectorNames.explain(r.error));
    } catch (e) {
      app.showToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(undefined);
    }
  };

  const authorize = async (name: string) => {
    if (!app.api) return;
    setBusy(name);
    // 先开窗口再填地址：异步之后再 window.open 会被浏览器当成弹窗拦掉
    const win = window.open("", "_blank");
    try {
      const flow = await startMCPOAuth(app.api, name, app.selectedProfile);
      if (!flow.authorizationURL) { win?.close(); app.showToast(flow.error ? `无法开始授权：${flow.error}` : "服务器没有返回授权页面"); return; }
      if (win) win.location.href = flow.authorizationURL;
      setOauth({ name, flowID: flow.flowID, url: flow.authorizationURL });
    } catch (e) {
      win?.close();
      app.showToast(`无法开始授权：${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(undefined);
    }
  };

  // 授权页是服务器自己接回调：这里轮询结果
  useEffect(() => {
    if (!oauth || !app.api) return;
    const api = app.api;
    let stopped = false;
    const timer = setInterval(async () => {
      try {
        const flow = await mcpOAuthStatus(api, oauth.flowID);
        if (stopped) return;
        if (flow.status === "approved") {
          stopped = true;
          setOauth(undefined);
          app.showToast(`${ConnectorNames.title(oauth.name)} 已连接`);
          reloadMCP(api).catch(() => {});
          void load();
        } else if (flow.status === "error") {
          stopped = true;
          setOauth(undefined);
          app.showToast(`授权失败：${flow.error ?? "未知错误"}`);
        }
      } catch { /* 下次再查 */ }
    }, 1500);
    return () => { stopped = true; clearInterval(timer); };
  }, [oauth?.flowID]); // eslint-disable-line react-hooks/exhaustive-deps

  const install = async (entry: MCPCatalogEntry, env: Record<string, string>) => {
    if (!app.api) return;
    setBusy(entry.name);
    try {
      await installMCP(app.api, entry.name, env, app.selectedProfile);
      await load();
      if (entry.auth_type === "oauth") await authorize(entry.name);
      else { await test(entry.name); reloadMCP(app.api).catch(() => {}); }
    } catch (e) {
      app.showToast(`连接失败：${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(undefined);
    }
  };

  const connect = (entry: MCPCatalogEntry) => {
    if (entry.auth_type === "api_key" || entry.required_env.some((v) => v.required !== false)) { setValues({}); setApiKeyFor(entry); }
    else void install(entry, {});
  };

  const remove = async (name: string) => {
    if (!app.api) return;
    try { await removeMCP(app.api, name, app.selectedProfile); reloadMCP(app.api).catch(() => {}); await load(); }
    catch (e) { app.showToast(e instanceof Error ? e.message : String(e)); }
  };

  const catalogRow = (e: MCPCatalogEntry) => (
    <PlainRow key={e.name} letter={ConnectorNames.letter(e.name)} title={ConnectorNames.title(e.name)} subtitle={ConnectorNames.subtitle(e.name) ?? e.description}
      trailing={busy === e.name ? <Spinner /> : !app.isGuest ? <PillButton title="连接" onClick={() => connect(e)} /> : undefined} />
  );

  return (
    <div className="skills-list">
      {error && <div className="t-footnote t-danger skills-pad">{error}</div>}
      {!catalog && !error && <div className="center-pad"><Spinner /></div>}
      {oauth && (
        <div className="oauth-banner t-footnote">
          <Spinner size={14} />
          <span style={{ flex: 1 }}>正在等待 {ConnectorNames.title(oauth.name)} 授权完成…</span>
          <a href={oauth.url} target="_blank" rel="noopener noreferrer">重新打开</a>
          <button className="t-2" onClick={() => { if (app.api) void cancelMCPOAuth(app.api, oauth.flowID); setOauth(undefined); }}>取消</button>
        </div>
      )}
      {connected.length > 0 && <div className="skills-section"><SectionTitle title="已连接" /></div>}
      {connected.map((x) => (
        <ConnectedRow key={x.name} server={x} busy={busy === x.name} guest={app.isGuest}
          onTest={() => void test(x.name)} onAuthorize={() => void authorize(x.name)}
          onMenu={(pos) => { setMenuServer(x); menu.show(pos); }} />
      ))}
      {recommended.length > 0 && <div className="skills-section"><SectionTitle title="推荐" /></div>}
      {recommended.map(catalogRow)}
      {others.length > 0 && (
        <div className="skills-section"><SectionTitle title="更多" trailing={!q ? <button className="t-footnote t-2" onClick={() => setShowOthers((v) => !v)}>{showOthers ? "收起" : `展开 ${others.length} 个`}</button> : undefined} /></div>
      )}
      {(showOthers || q) && others.map(catalogRow)}
      {catalog && !connected.length && !available.length && q && <div className="t-footnote t-2 skills-pad">没有找到</div>}
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} align="start" width={170} items={menuServer && !app.isGuest ? [
        { label: "测试连接", icon: <Check size={16} />, onSelect: () => void test(menuServer.name) },
        ...(mcpNeedsAuthorization(menuServer) ? [{ label: "授权", icon: <KeyRound size={16} />, onSelect: () => void authorize(menuServer.name) }] : []),
        { label: "断开", icon: <Link2 size={16} />, destructive: true, onSelect: () => void remove(menuServer.name) },
      ] : []} />
      <Sheet open={apiKeyFor !== undefined} onClose={() => setApiKeyFor(undefined)} size="medium" title={apiKeyFor ? `连接 ${ConnectorNames.title(apiKeyFor.name)}` : ""}
        leading={<button className="sheet-done" onClick={() => setApiKeyFor(undefined)}>取消</button>}
        trailing={<button className="sheet-done semibold" disabled={!apiKeyFor || apiKeyFor.required_env.some((v) => v.required !== false && !values[v.name])}
          onClick={() => { const e = apiKeyFor!; setApiKeyFor(undefined); void install(e, Object.fromEntries(Object.entries(values).filter(([, v]) => v))); }}>连接</button>}>
        <div className="form">
          <div className="form-card">
            {apiKeyFor?.required_env.map((v) => (
              <label key={v.name} className="env-field">
                <span className="t-caption t-2">{v.prompt ?? v.name}</span>
                <input type="password" autoComplete="off" placeholder={v.name} value={values[v.name] ?? ""} onChange={(e) => setValues({ ...values, [v.name]: e.target.value })} />
              </label>
            ))}
          </div>
          <span className="t-caption t-2 form-hint">密钥只保存在你的服务器上，网页不会保存。</span>
        </div>
      </Sheet>
    </div>
  );
}

function ConnectedRow({ server, busy, guest, onTest, onAuthorize, onMenu }: {
  server: MCPServer; busy: boolean; guest: boolean; onTest: () => void; onAuthorize: () => void; onMenu: (pos: { x: number; y: number }) => void;
}) {
  const press = useLongPress(onMenu);
  const needsAuth = mcpNeedsAuthorization(server);
  return (
    <div {...(guest ? {} : press)}>
      <PlainRow letter={ConnectorNames.letter(server.name)} title={ConnectorNames.title(server.name)}
        subtitle={needsAuth ? "需要授权" : server.enabled ? "已连接" : "已停用"} dimmed={!server.enabled}
        onClick={guest ? undefined : onTest}
        trailing={busy ? <Spinner /> : needsAuth && !guest ? <PillButton title="授权" prominent onClick={onAuthorize} />
          : <span className="status-dot" style={{ background: server.enabled ? "var(--success)" : "var(--text-3)" }} />} />
    </div>
  );
}
